import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@govtechsg/sgds-react/Button'
//import { Footer } from '@govtechsg/sgds-react'
import { Helmet } from "react-helmet-async";
import { CopyBlock , a11yLight, github} from 'react-code-blocks';

// Components
import HeaderRegComponent from '../../Components/HeaderRegComponent'
import FooterComponent from '../../Components/FooterComponent'

const TokenManagement = () => { 
    const codeSnippet = `
        const getAccessToken = async () => {
            try {
                const response = await axios.post(
                \`\${process.env.ONEMAP_BASE_URL}/api/auth/post/getToken\`,
                {
                    email: process.env.ONEMAP_API_EMAIL,
                    password: process.env.ONEMAP_API_PASSWORD,
                }
                );

                const accessToken = response.data.access_token;
                setAccessToken(accessToken); // Cache the token for reuse
                return accessToken;
            } catch (error) {
                console.error("Error refreshing token:", error);
                throw new Error("Unable to refresh token");
            }
        };`;
    const codeSnippet2 = `
        // Example utility functions
        let inMemoryToken = null;

        export const setAccessToken = (token) => {
        inMemoryToken = token;
        };

        export const getInMemoryAccessToken = () => inMemoryToken;
    `;

    const codeSnippet3 = `
        import axios from "axios";
        import https from "https";

        export default async function handler(req, res) {
        const { method } = req;
        const targetUrl = \`\${process.env.ONEMAP_BASE_URL}/\${req.query.externalUrl}\`;
        let access_token = getInMemoryAccessToken();

        try {
            const response = await axios({
            method,
            url: targetUrl,
            headers: {
                Authorization: \`\${access_token}\`,
                "Content-Type": "application/json",
            },
            params: req.query,
            });

            res.status(response.status).json(response.data);
        } catch (error) {
            if (error.response?.status === 401) {
            try {
                access_token = await getAccessToken();
                const retryResponse = await axios({
                method,
                url: targetUrl,
                headers: {
                    Authorization: \`\${access_token}\`,
                    "Content-Type": "application/json",
                },
                params: req.query,
                });

                res.status(retryResponse.status).json(retryResponse.data);
            } catch (retryError) {
                res.status(retryError.response?.status || 500).json({
                message: retryError.message,
                details: retryError.response?.data,
                });
            }
            } else {
            res.status(error.response?.status || 500).json({
                message: error.message,
                details: error.response?.data,
            });
            }
        }
        }
    `;
  	return (
		<>
		<Helmet>
        <title>Managing and Refreshing OneMap Token Validity| OneMap API</title>
        <meta
          name="description"
          content="This article aim to share some good practice to manage OneMap Token lifecycle and validity"
        />
      </Helmet>
		<div className="om-body container-fluid">

			<HeaderRegComponent />

			<div className="om-body-content pageBase">
                <div className='blog center om-body-container'>
                    <div className='row mb-4'>
                        <div class="col-md-10 offset-md-1">
                        <h1>Managing and Refreshing OneMap Token Validity in NodeJS</h1>
                        <p>The OneMap API requires a valid access token for each API request, and tokens need to be refreshed every 72 hours. This tutorial will guide you through the steps to properly authenticate and handle API requests in NodeJS. However the general principle and algorithm applied in this guide is also applicable to other platforms and programming languages.</p>

                        <p>The tutorial below is based on snippet built on Next.JS with a react frontend framework. We setup a middleware proxy which will take care of calling OneMap API and refreshing the token in a single location.</p>

                        <h3>1. Prerequisites</h3>
                        <p>Before you start, ensure you have the following:
                            <ul>
                                <li>A <a href="/apidocs/register">registered</a> account for the OneMap API.</li>
                                <li>Your email and password to generate tokens.</li>
                                <li>A development environment with Node.js and npm/yarn installed.</li>
                                <li>axios and https libraries installed in your project.</li>
                            </ul>
                        </p>

                        <h3>2. Setting Up Environment Variables</h3>
                        <p>Store sensitive information, such as your email, password, and API base URL, in environment variables for security:</p>
                        <code>
                            ONEMAP_API_EMAIL=your_email@example.com<br/>
                            ONEMAP_API_PASSWORD=your_password<br/>
                            ONEMAP_BASE_URL=https://www.onemap.gov.sg
                        </code>

                        <h3>3. Token Management</h3>
                        <p>To call the OneMap API, a valid token is required. Here's how to manage token retrieval and caching:</p>

                        <h4>Retrieving the Access Token</h4>
                        The getAccessToken function authenticates with OneMap and retrieves an access token:
                        <div class='codeblock'>
                            <CopyBlock
                                className="abc"
                                text={codeSnippet}
                                language="javascript"
                                theme={github}
                                wrapLines={true}
                                codeBlock
                                />
                        </div>

                        <h4>Storing Tokens in Memory</h4>
                        In order to improve API speed and avoid overloading OneMap API which might result in rate limited ban, you can use a utility like tokenCache to store the token temporarily for reuse without additional API calls.
                        <div class='codeblock'>
                            <CopyBlock
                                className="abc"
                                text={codeSnippet2}
                                language="javascript"
                                theme={github}
                                wrapLines={true}
                                codeBlock
                                />
                        </div>


                        <h3>4. Making The API Calls</h3>
                        <p>You may put the code below under <code>src/pages/api/om/[...externalUrl].js </code> and call all OneMap API via this proxy.
                            Wrap your API call logic in a handler to manage requests and handle token expiry:</p>
                        <h4>Handling API Requests</h4>
                        <p>This handler proxies API requests and refreshes tokens when needed:</p>
                        <div class='codeblock'>
                            <CopyBlock
                                className="abc"
                                text={codeSnippet3}
                                language="javascript"
                                theme={github}
                                wrapLines={true}
                                codeBlock
                                />
                        </div><br/>
                        <p>The code above acts as a proxy for API calls to OneMap, handling token-based authentication with retry logic.</p>
                        <ol>
                            <li>
                                <b><strong>Primary Request</strong>:</b>
                                <ul>
                                    <li>Sends the request to the API with:
                                        <ul>
                                            <li>URL, HTTP method, headers (Bearer token), query params, and HTTPS agent.
                                            </li>
                                        </ul>
                                    </li>
                                    <li>On success, returns the response to the client.</li>
                                </ul>
                            </li>
                            <li>
                                <b><strong>Error Handling</strong>:</b>
                                <ul>
                                    <li><strong>401 Errors</strong>: It means the token might be expired. Refreshes the token and retries the request.
                                    </li>
                                    <li><strong>Other Errors</strong>: Returns error details (status, message, and response data).
                                    </li>
                                </ul>
                            </li>
                        </ol>
                        <strong>Tips: </strong>If you have issue with self signed certificate in development environment, you can consider setting the httpsAgent. However this is <b>NOT</b> recommended in production environment.<br/>
                        <code>{`const httpsAgent = new https.Agent({ rejectUnauthorized: false });`}</code>

                        <h3>5. Recommended Practices</h3>
                        <ul>
                            <li>
                                Secure Sensitive Data: Always store credentials and tokens securely using environment variables or secure storage solutions.
                            </li>
                            <li>Efficient Token Use: Avoid repeated calls to /getToken to prevent rate-limiting bans.</li>
                            <li>Graceful Token Refresh: Automatically renew tokens to ensure uninterrupted API usage.</li>
                            <li>SSL Validation: Use rejectUnauthorized: false only in development, never in production.</li>
                        </ul>

                        <p>Developer can <a href="http://www.onemap.gov.sg/apidocs/contactus">contact us</a> if you have any questions regarding OneMap API.</p>
                        <div style={{marginBottom:"7em"}}/>
 
                    </div>
                </div>
            </div>
        </div>

			<FooterComponent />

		</div>
		</>
	)
}

export default TokenManagement